import { useMemo } from "react";
import { useInfiniteQuery } from "@tanstack/react-query";
import { api } from "../lib/api";

export interface CatalogProductOption {
  id: string;
  sku?: string;
  name: string;
  stockAvailable?: number;
  active?: boolean;
  images?: string[];
  price?: number;
  behavior?: string;
}

interface CatalogProductsPage {
  items: CatalogProductOption[];
  total: number;
  page: number;
  pageSize: number;
}

const DEFAULT_PAGE_SIZE = 200;

/** Paginated catalog products for SearchSelect — 200/page, infinite scroll + search. */
export function useInfiniteCatalogProducts(options: {
  enabled: boolean;
  search: string;
  /** Extra query params (without page/pageSize/search). */
  params?: Record<string, string>;
  queryKey: unknown[];
  pageSize?: number;
}) {
  const pageSize = options.pageSize ?? DEFAULT_PAGE_SIZE;
  const search = options.search.trim();
  const paramsKey = JSON.stringify(options.params ?? {});

  const query = useInfiniteQuery({
    queryKey: ["catalog-products-infinite", ...options.queryKey, search, pageSize, paramsKey],
    enabled: options.enabled,
    initialPageParam: 1,
    queryFn: async ({ pageParam }) => {
      const params = new URLSearchParams({
        page: String(pageParam),
        pageSize: String(pageSize),
        sort: "name",
        ...(options.params ?? {}),
      });
      if (search) params.set("search", search);
      return api<CatalogProductsPage>(`/catalog/products?${params}`);
    },
    getNextPageParam: (lastPage, allPages) => {
      const loaded = allPages.reduce((n, page) => n + page.items.length, 0);
      return loaded < lastPage.total ? allPages.length + 1 : undefined;
    },
  });

  const items = useMemo(
    () => query.data?.pages.flatMap((page) => page.items) ?? [],
    [query.data?.pages],
  );

  const total = query.data?.pages[0]?.total ?? 0;

  return {
    items,
    total,
    isLoading: query.isLoading,
    isError: query.isError,
    isFetchingNextPage: query.isFetchingNextPage,
    hasNextPage: Boolean(query.hasNextPage),
    fetchNextPage: () => {
      if (query.hasNextPage && !query.isFetchingNextPage) {
        void query.fetchNextPage();
      }
    },
  };
}
